import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { SceneObject, SceneSource } from '../core/scene';
import { saveRecentHandle } from '../services/fileSystem/recentHandleStore';

/**
 * Document Store
 *
 * Two roles:
 * 1. Document metadata — the state of the currently open document (project
 *    or project template): its display name, kind, dirty flag and where it
 *    lives on disk (a File System Access handle or a virtual-FS entry id).
 *    The File menu (Save / Save As / Open) reads and updates this store.
 * 2. Scene ledger (Phase A2) — `sceneObjects` is the single cross-workbench
 *    registry of every object in the open document, speaking the shared
 *    SceneObject protocol. Each workbench keeps owning its rich model
 *    (kernel project for BIM, app-store map for CAD) but MUST mirror every
 *    create/update/delete into this ledger. Shared consumers (picking,
 *    VisibilityResolver, cross-mode overlays, persistence of non-BIM
 *    content) read the ledger, never the mod stores.
 */

export type DocumentKind = 'project' | 'template';

export interface RecentFile {
    id: string;
    name: string;
    kind: DocumentKind;
    savedAt: string;
    /** Virtual-FS entry id when the document lives in the in-app workspace. */
    vfsFileId?: string | null;
    /** True when a native FileSystemFileHandle is parked in recentHandleStore. */
    hasHandle?: boolean;
}

export interface PendingSave {
    /** Serialized .yap/.yate envelope waiting to be written by the browser dialog. */
    envelopeText: string;
    suggestedName: string;
    kind: DocumentKind;
}

interface DocumentState {
    kind: DocumentKind;
    displayName: string;
    isDirty: boolean;
    /** Native file handle when the document lives on the real file system. */
    fileHandle: FileSystemFileHandle | null;
    /** Virtual-FS entry id when the document lives in the in-app workspace. */
    vfsFileId: string | null;
    /** Envelope queued for the fallback save dialog (Save As without FS Access). */
    pendingSave: PendingSave | null;
    recents: RecentFile[];

    /**
     * Cross-workbench scene ledger (Phase A2). Id-keyed SceneObject records;
     * see file header for the mirroring contract. Session-only by design:
     * reloads rehydrate it from the owning stores during load.
     */
    sceneObjects: Record<string, SceneObject>;

    setDocument: (options: {
        kind?: DocumentKind;
        displayName: string;
        fileHandle?: FileSystemFileHandle | null;
        vfsFileId?: string | null;
    }) => void;
    markDirty: () => void;
    clearDirty: () => void;
    setPendingSave: (pending: PendingSave | null) => void;
    pushRecent: (entry: {
        name: string;
        kind: DocumentKind;
        vfsFileId?: string | null;
        fileHandle?: FileSystemFileHandle | null;
    }) => void;
    removeRecent: (id: string) => void;

    // --- Scene ledger actions ---
    /** Register or replace one protocol record (idempotent, mirror-safe). */
    registerSceneObject: (obj: SceneObject) => void;
    /** Patch the protocol-level view of a record; ignores unknown ids. */
    patchSceneObject: (id: string, patch: Partial<Omit<SceneObject, 'id' | 'source'>>) => void;
    /** Drop one record from the ledger; no-op when the id belongs to another source. */
    removeSceneObject: (id: string, source?: SceneSource) => void;
    /** Bulk upsert (session rehydration): one write for N records. */
    upsertSceneObjects: (objects: SceneObject[]) => void;
    /** Bulk remove (session rehydration); same source guard as removeSceneObject. */
    removeSceneObjects: (ids: string[], source?: SceneSource) => void;
    /** Replace the whole ledger (bulk load / reset / rehydration). */
    replaceSceneLedger: (objects: SceneObject[]) => void;
    /**
     * Replace only the records owned by one workbench source (bulk mirror /
     * rehydration). Other sources' records stay untouched.
     */
    replaceSceneLedgerSource: (source: SceneSource, objects: SceneObject[]) => void;
    /** Clear every record (new/opened document before rehydration). */
    clearSceneLedger: () => void;
}

export const useDocumentStore = create<DocumentState>()(
    persist(
        (set, get) => ({
            kind: 'project',
            displayName: 'Project 1',
            isDirty: false,
            fileHandle: null,
            vfsFileId: null,
            pendingSave: null,
            recents: [],
            sceneObjects: {},

            setDocument: ({ kind, displayName, fileHandle, vfsFileId }) => {
                set((state) => ({
                    kind: kind ?? state.kind,
                    displayName,
                    fileHandle: fileHandle ?? null,
                    vfsFileId: vfsFileId ?? null,
                    pendingSave: null,
                    isDirty: false,
                    // The scene ledger is NOT wiped here: load flows call
                    // replaceSceneLedgerSource per owner right after the
                    // kernel project is replaced, and a blanket clear would
                    // race with that rehydration.
                }));
            },

            markDirty: () => {
                if (!get().isDirty) set({ isDirty: true });
            },

            clearDirty: () => {
                if (get().isDirty) set({ isDirty: false });
            },

            setPendingSave: (pendingSave) => set({ pendingSave }),

            pushRecent: ({ name, kind, vfsFileId, fileHandle }) => {
                // Keep one entry per (name, kind): refresh its identity in
                // place so the stable id continues to point at the stored
                // native handle. A brand-new entry mints a fresh id.
                const existing = get().recents.find((r) => r.name === name && r.kind === kind);
                const id = existing?.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                if (fileHandle) {
                    void saveRecentHandle(id, fileHandle).catch(() => { /* best-effort */ });
                }
                const entry: RecentFile = {
                    id,
                    name,
                    kind,
                    savedAt: new Date().toISOString(),
                    vfsFileId: vfsFileId ?? existing?.vfsFileId ?? null,
                    hasHandle: fileHandle ? true : existing?.hasHandle ?? false,
                };
                set((state) => ({
                    recents: [entry, ...state.recents.filter((r) => r.id !== id)].slice(0, 12),
                }));
            },

            removeRecent: (id) => {
                set((state) => ({ recents: state.recents.filter((r) => r.id !== id) }));
            },

            // --- Scene ledger actions ---
            registerSceneObject: (obj) => {
                set((state) => ({
                    sceneObjects: { ...state.sceneObjects, [obj.id]: obj },
                }));
            },

            patchSceneObject: (id, patch) => {
                const existing = get().sceneObjects[id];
                if (!existing) return;
                set((state) => ({
                    sceneObjects: {
                        ...state.sceneObjects,
                        [id]: { ...existing, ...patch, id: existing.id, source: existing.source },
                    },
                }));
            },

            removeSceneObject: (id, source) => {
                const existing = get().sceneObjects[id];
                if (!existing) return;
                // Source guard: a delete from one workbench must not wipe a
                // same-id record owned by another (defensive; ids are unique).
                if (source && existing.source !== source) return;
                set((state) => {
                    const next = { ...state.sceneObjects };
                    delete next[id];
                    return { sceneObjects: next };
                });
            },

            upsertSceneObjects: (objects) => {
                if (objects.length === 0) return;
                set((state) => {
                    const next = { ...state.sceneObjects };
                    for (const obj of objects) next[obj.id] = obj;
                    return { sceneObjects: next };
                });
            },

            removeSceneObjects: (ids, source) => {
                set((state) => {
                    const doomed = new Set(ids);
                    let changed = false;
                    const next: Record<string, SceneObject> = {};
                    for (const [id, obj] of Object.entries(state.sceneObjects)) {
                        if (doomed.has(id) && (!source || obj.source === source)) {
                            changed = true;
                            continue;
                        }
                        next[id] = obj;
                    }
                    return changed ? { sceneObjects: next } : {};
                });
            },

            replaceSceneLedger: (objects) => {
                set({
                    sceneObjects: Object.fromEntries(objects.map((o) => [o.id, o])),
                });
            },

            replaceSceneLedgerSource: (source, objects) => {
                set((state) => {
                    const kept: Record<string, SceneObject> = {};
                    for (const [id, obj] of Object.entries(state.sceneObjects)) {
                        if (obj.source !== source) kept[id] = obj;
                    }
                    for (const obj of objects) kept[obj.id] = obj;
                    return { sceneObjects: kept };
                });
            },

            clearSceneLedger: () => set({ sceneObjects: {} }),
        }),
        {
            name: 'yap-document-store',
            // Only persist the recents list; handles/paths are session-scoped,
            // the dirty flag must never survive a reload, and the scene ledger
            // is rehydrated from the owning stores on load.
            partialize: (state) => ({ recents: state.recents }) as DocumentState,
        }
    )
);

/**
 * Warn before closing the tab while there are unsaved changes (mirrors
 * BIM's save prompt on exit).
 */
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', (e) => {
        if (!useDocumentStore.getState().isDirty) return;
        e.preventDefault();
        e.returnValue = '';
    });
}
